import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js';
import { motion } from "framer-motion";

const MotionButton = motion(Button);
const MotionDiv = motion.div;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const Pricing = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  const handleSubscribe = async (priceId: string) => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }

    try {
      console.log('Starting subscription process for user:', user.id);
      console.log('Using price ID:', priceId);

      // Get the current session - needed for the auth token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('Session error:', sessionError);
        throw new Error('Authentication session not found or expired. Please log in again.');
      }

      toast({
        title: "Processing",
        description: "Redirecting to checkout...",
      });

      // Prepare the body to be sent to the Edge Function
      const body = {
        priceId,
        planName: pricingPlans.find(plan => plan.priceId === priceId)?.name || 'Unknown',
        metadata: { // Optional: Add any frontend-specific metadata here
          source: 'pricing_page'
        }
      };

      console.log('Invoking create-checkout with body:', body);

      // Use supabase.functions.invoke as recommended
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: body, // Pass the JS object directly
        // No need to manually set Content-Type or Authorization, invoke handles it
      });

      console.log('Supabase function response:', { data, error });

      if (error) {
        // Handle specific function invocation errors
        if (error instanceof FunctionsHttpError) {
          const errorMessage = await error.context.json();
          console.error('Function returned an error:', errorMessage);
          throw new Error(errorMessage.error || 'Checkout creation failed.');
        } else if (error instanceof FunctionsRelayError) {
          console.error('Relay error:', error.message);
          throw new Error('Network error connecting to the checkout service.');
        } else if (error instanceof FunctionsFetchError) {
          console.error('Fetch error:', error.message);
          throw new Error('Error fetching the checkout service.');
        } else {
          console.error('Generic function error:', error);
          throw new Error('Failed to create checkout session.');
        }
      }

      if (!data?.url) {
        console.error('Invalid response data - missing URL:', data);
        throw new Error('Invalid response from checkout service.');
      }

      // Redirect to Stripe checkout
      console.log('Redirecting to Stripe checkout URL:', data.url);
      window.location.href = data.url;

    } catch (error) {
      console.error('Subscription process error:', error);
      toast({
        title: "Subscription Error",
        description: error instanceof Error ? error.message : "Failed to start subscription process. Please try again.",
        variant: "destructive"
      });
    }
  };

  const pricingPlans = [
    {
      name: "Free",
      price: "$0",
      description: "Try out AI image generation",
      features: [
        "2 credits (low quality)",
        "1024x1024 resolution",
        "Basic styles",
        "Community support",
        "No credit card required"
      ],
      limitations: [
        "Limited to low quality only",
        "No commercial usage",
        "Credits expire in 24 hours"
      ],
      priceId: null,
      cta: isAuthenticated ? "Current Plan" : "Get Started",
      popular: false,
    },
    {
      name: "Pro",
      price: "$19.99",
      period: "/month",
      description: "Perfect for individual creators",
      features: [
        "400 credits per month",
        "All quality levels",
        "All resolutions",
        "All styles",
        "Priority generation",
        "Commercial usage rights",
        "Email support",
        "Image history saved for 30 days",
        "~$0.05 per credit"
      ],
      limitations: [],
      priceId: "price_1RJeriQVbLBxcoplXRqvs8u8",
      cta: "Subscribe Now",
      popular: true,
    },
    {
      name: "Business",
      price: "$99",
      period: "/month",
      description: "For teams and businesses",
      features: [
        "2000 credits per month",
        "All quality levels",
        "All resolutions",
        "All styles",
        "Priority generation",
        "API Access",
        "Dedicated support",
        "Custom model fine-tuning",
        "Unlimited image history",
        "~$0.049 per credit"
      ],
      limitations: [],
      priceId: "price_1RJerjQVbLBxcoplXRqvs8u8",
      cta: "Subscribe Now",
      popular: false,
    },
  ];

  const faqs = [
    {
      question: "How are credits calculated?",
      answer: "Credit consumption varies by quality level and image size. Low quality images use 1 credit, medium quality uses 2 credits, and high quality uses 4 credits. Larger resolutions (1024x1536 or 1536x1024) use additional credits proportionally."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel your subscription at any time. Your subscription will remain active until the end of the current billing period."
    },
    {
      question: "Do unused credits roll over?",
      answer: "No, unused credits do not roll over to the next month. They reset on the first day of each month to ensure consistent service quality."
    },
    {
      question: "Can I use the images commercially?",
      answer: "Free plan users cannot use images for commercial purposes. Pro and Business plans include full commercial usage rights for all generated images."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, including Visa, Mastercard, and American Express. We also support PayPal for subscription payments."
    },
    {
      question: "What are the different quality levels?",
      answer: "We offer three quality levels: Low (good for drafts and concepts), Medium (great for most use cases), and High (best for professional and commercial use). Higher quality images use more credits but provide better detail and clarity."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background via-background/95 to-background/90">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <MotionDiv
          className="absolute inset-0 -z-10 opacity-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 blur-3xl" />
        </MotionDiv>
        <div className="container mx-auto px-4">
          <MotionDiv
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.h1 
              className="text-5xl font-extralight mb-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Simple, Transparent Pricing
            </motion.h1>
            <motion.p 
              className="text-xl mb-8 text-muted-foreground font-light"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Choose the plan that works best for you. No hidden fees or surprises.
            </motion.p>
          </MotionDiv>
        </div>
      </section>
      
      {/* Pricing Plans */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <MotionDiv
            className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
            variants={container}
            initial="hidden"
            animate="show"
          >
            {pricingPlans.map((plan, index) => (
              <MotionDiv
                key={index}
                variants={item}
                className={`relative group`}
              >
                <motion.div 
                  className={`h-full bg-card p-8 rounded-xl border backdrop-blur-sm
                    ${plan.popular ? 'border-primary shadow-lg shadow-primary/10' : 'border-border/50'}
                    hover:border-primary/50 transition-all duration-300
                    flex flex-col`}
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-500 px-4 py-1 rounded-full text-sm font-medium text-white">
                      Most Popular
                    </div>
                  )}
                  <div className="mb-8">
                    <h3 className="text-2xl font-light mb-2">{plan.name}</h3>
                    <div className="mb-2">
                      <span className="text-4xl font-light">{plan.price}</span>
                      {plan.period && <span className="text-muted-foreground font-light">{plan.period}</span>}
                    </div>
                    <p className="text-muted-foreground font-light">{plan.description}</p>
                  </div>
                  
                  <div className="flex-grow mb-8">
                    <h4 className="font-medium text-lg mb-4">Features</h4>
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <motion.li 
                          key={idx} 
                          className="flex items-start gap-2"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                        >
                          <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                          <span className="font-light">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                  
                  {plan.limitations.length > 0 && (
                    <div className="mb-8">
                      <h4 className="font-medium text-lg mb-4">Limitations</h4>
                      <ul className="space-y-3">
                        {plan.limitations.map((limitation, idx) => (
                          <motion.li 
                            key={idx} 
                            className="flex items-start gap-2 text-muted-foreground"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                          >
                            <span className="h-5 w-5 flex items-center justify-center shrink-0 mt-0.5">-</span>
                            <span className="font-light">{limitation}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <MotionButton 
                    className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white hover:opacity-90"
                    variant={plan.popular ? "default" : "outline"}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (plan.name === "Free") {
                        navigate(isAuthenticated ? "/dashboard" : "/signup");
                      } else if (plan.name === "Business") {
                        navigate("/contact");
                      } else if (plan.priceId && typeof plan.priceId === 'string') {
                        console.log('Initiating subscription for plan:', plan.name);
                        handleSubscribe(plan.priceId);
                      } else {
                        console.error('Invalid plan configuration:', plan);
                        toast({
                          title: "Configuration Error",
                          description: "This plan is not properly configured. Please try another plan or contact support.",
                          variant: "destructive"
                        });
                      }
                    }}
                  >
                    {plan.cta}
                  </MotionButton>
                </motion.div>
              </MotionDiv>
            ))}
          </MotionDiv>
        </div>
      </section>
      
      {/* FAQs */}
      <section className="py-16 bg-muted/50 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <MotionDiv
            className="max-w-3xl mx-auto"
            variants={container}
            initial="hidden"
            animate="show"
          >
            <motion.h2 
              className="text-3xl font-light mb-8 text-center bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent"
              variants={item}
            >
              Frequently Asked Questions
            </motion.h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <MotionDiv
                  key={index}
                  className="bg-card/50 p-6 rounded-xl border border-border/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300"
                  variants={item}
                >
                  <h3 className="text-xl font-light mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground font-light">{faq.answer}</p>
                </MotionDiv>
              ))}
            </div>
          </MotionDiv>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <MotionDiv
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-light mb-4">Ready to Start Creating?</h2>
            <p className="text-lg text-muted-foreground font-light mb-8">
              Join thousands of creators using our platform to bring their ideas to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <MotionButton 
                asChild 
                size="lg"
                className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white hover:opacity-90"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link to="/signup">Start Free Trial</Link>
              </MotionButton>
              <MotionButton 
                asChild 
                size="lg" 
                variant="outline"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link to="/contact">Contact Sales</Link>
              </MotionButton>
            </div>
          </MotionDiv>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
